import React, { useEffect, RefObject } from 'react'
import { tabbable } from 'tabbable'

interface Props {
  children: React.ReactNode | any
  focusFirst?: boolean
}

export default function FocusTrap({ children, focusFirst = false }: Props) {
  const root: RefObject<any> = React.useRef()
  const anchor: RefObject<any> = React.useRef(document.activeElement)
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const returnFocus = () => {
    // Returns focus to the last focused element prior to trap.
    if (anchor.current) {
      anchor.current.focus()
    }
  }

  const trapFocus = () => {
    // Focus the container element
    if (root.current) {
      // root.current.focus() // do not focus root as it results in scrolling to mid of container if it overflows (scrollable)
      if (focusFirst) {
        selectFirstFocusableEl()
      }
    }
  }

  const selectFirstFocusableEl = () => {
    // Try to find focusable elements, if match then focus
    // Up to 6 seconds of load time threshold
    let match = false
    let end = 60 // Try to find match at least n times
    let i = 0
    intervalRef.current = setInterval(() => {
      // The trap may have unmounted (modal closed) while this interval is
      // still pending — `root.current` is then null and `tabbable(null)`
      // would throw. Bail out and clear the timer.
      if (!root.current) {
        if (intervalRef.current) clearInterval(intervalRef.current)
        return
      }
      if (!match !== i > end) {
        const focusables = tabbable(root.current)
        match = !!focusables.length
        if (match) {
          // Attempt to focus the first el
          focusables[0].focus()
        }
        i = i + 1
      } else {
        // Clear interval after n attempts
        if (intervalRef.current) clearInterval(intervalRef.current)
      }
    }, 100)
  }

  // Mount/unmount ONLY. Without a dependency array this effect re-ran after
  // every render of the trapped subtree — so each keystroke in a modal input
  // first ran the cleanup (returnFocus() → the element focused before the
  // modal opened, i.e. the trigger button) and then re-armed trapFocus, which
  // moved the caret to the first focusable field. Typing in the second field
  // of a modal form was impossible.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    timeoutRef.current = setTimeout(trapFocus, 20)
    return () => {
      // Tear down any pending timers so nothing runs after unmount.
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (intervalRef.current) clearInterval(intervalRef.current)
      returnFocus()
    }
  }, [])

  return React.createElement(
    'div',
    {
      ref: root,
      className: 'outline-none focus-trap',
      tabIndex: -1,
    },
    children
  )
}
